import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  ORDER_STATUS_LABEL,
  ORDER_STATUSES_FOR_ADMIN,
} from "@shared/orderStatus";
import { formatKRW } from "@shared/pricing";
import { Image, Loader2, Package, Phone, Truck, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Link, useRoute } from "wouter";
import type { OrderStatus } from "../../../../drizzle/schema";
import { AdminLayout } from "./AdminLayout";

const STATUS_COLORS: Record<string, string> = {
  paid: "bg-blue-100 text-blue-800",
  reviewing: "bg-yellow-100 text-yellow-800",
  producing: "bg-orange-100 text-orange-800",
  ready_to_ship: "bg-purple-100 text-purple-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export function AdminOrdersListPage() {
  const orders = trpc.admin.listOrders.useQuery();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredOrders = (orders.data ?? []).filter(
    (o) => statusFilter === "all" || o.status === statusFilter,
  );

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="font-serif text-2xl text-primary">주문 관리</h1>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">상태:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">전체</SelectItem>
              {ORDER_STATUSES_FOR_ADMIN.map((s) => (
                <SelectItem key={s} value={s}>{ORDER_STATUS_LABEL[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">
            {filteredOrders.length}건
          </div>
        </div>
      </div>

      {orders.isLoading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="py-20 text-center text-muted-foreground">주문이 없습니다.</div>
      ) : (
        <div className="space-y-2">
          {/* Desktop Table Header */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-muted-foreground border-b border-border/60">
            <div className="col-span-1">이미지</div>
            <div className="col-span-2">주문번호</div>
            <div className="col-span-2">고객</div>
            <div className="col-span-2">주문상태</div>
            <div className="col-span-1">결제</div>
            <div className="col-span-2">배송</div>
            <div className="col-span-1">금액</div>
            <div className="col-span-1"></div>
          </div>

          {filteredOrders.map((o) => (
            <div
              key={o.id}
              className="border border-border/60 rounded-lg hover:border-primary/30 hover:bg-primary/[0.02] transition-all"
            >
              {/* Mobile Layout */}
              <div className="lg:hidden p-3 space-y-2">
                <div className="flex items-start gap-3">
                  {o.imageUrl ? (
                    <img src={o.imageUrl} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-muted flex items-center justify-center shrink-0">
                      <Image className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-mono">{o.orderNo}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] ?? "bg-gray-100 text-gray-700"}`}>
                        {ORDER_STATUS_LABEL[o.status]}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(o.createdAt).toLocaleDateString("ko-KR")} · {formatKRW(o.totalAmount)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground flex items-center gap-3">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{(o as any).userName ?? "미확인"}</span>
                    {o.trackingNumber && <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{o.trackingNumber}</span>}
                  </div>
                  <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                    <Link href={`/admin/orders/${o.id}`}>관리</Link>
                  </Button>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden lg:grid lg:grid-cols-12 gap-2 px-4 py-3 items-center">
                <div className="col-span-1">
                  {o.imageUrl ? (
                    <img src={o.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                      <Image className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <div className="text-sm font-mono">{o.orderNo}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(o.createdAt).toLocaleDateString("ko-KR")}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm">{(o as any).userName ?? "미확인"}</div>
                  <div className="text-xs text-muted-foreground truncate">{(o as any).userEmail ?? ""}</div>
                </div>
                <div className="col-span-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] ?? "bg-gray-100 text-gray-700"}`}>
                    {ORDER_STATUS_LABEL[o.status]}
                  </span>
                </div>
                <div className="col-span-1">
                  <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">완료</span>
                </div>
                <div className="col-span-2 text-xs text-muted-foreground">
                  {o.trackingNumber ? (
                    <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{o.trackingCarrier ?? ""} {o.trackingNumber}</span>
                  ) : (
                    <span className="opacity-50">미입력</span>
                  )}
                </div>
                <div className="col-span-1 text-sm font-medium">
                  {formatKRW(o.totalAmount)}
                </div>
                <div className="col-span-1 text-right">
                  <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                    <Link href={`/admin/orders/${o.id}`}>관리</Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminOrderDetailPage() {
  const [, params] = useRoute<{ id: string }>("/admin/orders/:id");
  const id = Number(params?.id ?? 0);
  const utils = trpc.useUtils();
  const detail = trpc.orders.detail.useQuery({ id }, { enabled: id > 0 });
  const update = trpc.admin.updateOrder.useMutation({
    onSuccess: () => {
      toast.success("주문 정보를 업데이트했습니다.");
      utils.orders.detail.invalidate({ id });
      utils.admin.listOrders.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const [status, setStatus] = useState<OrderStatus | "">("");
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [memo, setMemo] = useState("");

  if (detail.isLoading)
    return (
      <AdminLayout>
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  if (!detail.data)
    return (
      <AdminLayout>
        <div className="text-muted-foreground">주문을 찾을 수 없습니다.</div>
      </AdminLayout>
    );

  const { order, items, shipping, payments } = detail.data;
  const submit = () => {
    update.mutate({
      id: order.id,
      status: (status || order.status) as OrderStatus,
      trackingCarrier: carrier || order.trackingCarrier || undefined,
      trackingNumber: tracking || order.trackingNumber || undefined,
      memo: memo || order.memo || undefined,
    });
  };

  return (
    <AdminLayout>
      <Link
        href="/admin/orders"
        className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
      >
        ← 주문 목록
      </Link>
      <h1 className="font-serif text-2xl text-primary mt-2 mb-6">
        주문 #{order.orderNo}
      </h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column: Image + Payment */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Image className="w-3 h-3" /> 미리보기 이미지
              </div>
              {order.imageUrl ? (
                <img
                  src={order.imageUrl}
                  alt=""
                  className="w-full aspect-square object-cover rounded-lg"
                />
              ) : (
                <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                  이미지 없음
                </div>
              )}
              {/* V1: Service info & file downloads */}
              <div className="mt-3 space-y-2 text-sm">
                {(order as any).serviceType && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">서비스</span>
                    <span className="font-medium">{(order as any).serviceType === "oil_painting" ? "유화출력" : "실사출력"}</span>
                  </div>
                )}
                {(order as any).sizeLabel && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">사이즈</span>
                    <span className="font-medium">{(order as any).sizeLabel}</span>
                  </div>
                )}
                {(order as any).orientation && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">방향</span>
                    <span className="font-medium">{(order as any).orientation === "landscape" ? "가로형" : "세로형"}</span>
                  </div>
                )}
                {(order as any).resolutionStatus && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">해상도</span>
                    <span className={`font-medium ${
                      (order as any).resolutionStatus === "good" ? "text-green-600" :
                      (order as any).resolutionStatus === "fair" ? "text-yellow-600" : "text-red-600"
                    }`}>
                      {(order as any).resolutionStatus === "good" ? "좋음" :
                       (order as any).resolutionStatus === "fair" ? "보통" : "낮음"}
                    </span>
                  </div>
                )}
              </div>
              {/* File download buttons */}
              <div className="mt-3 space-y-2">
                {(order as any).originalFileKey && (
                  <a
                    href={`/manus-storage/${(order as any).originalFileKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg py-2 hover:bg-blue-100 transition-colors"
                  >
                    원본 파일 다운로드
                  </a>
                )}
                {(order as any).printFileKey && (
                  <a
                    href={`/manus-storage/${(order as any).printFileKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg py-2 hover:bg-green-100 transition-colors"
                  >
                    출력용 파일 다운로드
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                <Package className="w-3 h-3" /> 결제 내역
              </div>
              {payments.length > 0 ? (
                payments.map((p) => (
                  <div
                    key={p.id}
                    className="text-sm flex justify-between text-muted-foreground py-1"
                  >
                    <span>
                      {p.provider} · {p.status === "paid" ? "결제완료" : p.status}
                    </span>
                    <span className="font-medium text-foreground">{formatKRW(p.amount)}</span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">결제 정보 없음</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Details + Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <Card>
            <CardContent className="p-5">
              <div className="text-xs text-muted-foreground mb-3">주문 항목</div>
              <div className="space-y-1 text-sm">
                {items.map((i) => (
                  <div key={i.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {i.productName} <span className="text-xs">({i.productKind})</span>
                    </span>
                    <span>{formatKRW(i.unitPrice)}</span>
                  </div>
                ))}
                <div className="border-t border-border/60 mt-2 pt-2 flex justify-between text-base">
                  <span className="font-medium">총액</span>
                  <span className="font-serif text-xl text-primary">
                    {formatKRW(order.totalAmount)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Info */}
          {shipping && (
            <Card>
              <CardContent className="p-5">
                <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                  <Truck className="w-3 h-3" /> 배송 정보
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">수령인:</span>{" "}
                    <span className="font-medium">{shipping.recipientName}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Phone className="w-3 h-3 text-muted-foreground" />
                    <span>{shipping.phone}</span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">주소:</span>{" "}
                    [{shipping.postalCode}] {shipping.address1} {shipping.address2}
                  </div>
                  {shipping.note && (
                    <div className="sm:col-span-2 text-xs text-muted-foreground">
                      요청사항: {shipping.note}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status Update */}
          <Card className="border-primary/20">
            <CardContent className="p-5 space-y-4">
              <div className="font-serif text-lg text-primary flex items-center gap-2">
                상태 · 배송 업데이트
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-xs text-muted-foreground">
                    현재: <span className="font-medium text-foreground">{ORDER_STATUS_LABEL[order.status]}</span>
                  </Label>
                  <Select
                    value={status || order.status}
                    onValueChange={(v) => setStatus(v as OrderStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="상태 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_STATUSES_FOR_ADMIN.map((s) => (
                        <SelectItem key={s} value={s}>
                          {ORDER_STATUS_LABEL[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs text-muted-foreground">
                    택배사
                  </Label>
                  <Input
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    placeholder={order.trackingCarrier ?? "CJ대한통운"}
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs text-muted-foreground">
                    운송장 번호
                  </Label>
                  <Input
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder={order.trackingNumber ?? "1234567890"}
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs text-muted-foreground">
                    메모
                  </Label>
                  <Input
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder={order.memo ?? "내부 메모"}
                  />
                </div>
              </div>

              <Button onClick={submit} disabled={update.isPending} className="w-full sm:w-auto">
                {update.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                업데이트 저장
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
